import type { InspectionOutcome } from '../generated/enums';
import type { DamageChecklistLine, ReturnInspection } from '../api/inspections';

/**
 * One line of the checklist as the counter is working it.
 *
 * <p>
 * Every kind is a row whether or not it applies, because the checklist is the thing being worked
 * down — a clerk reads the list and ticks. Modelling only the ticked ones would mean the screen
 * rebuilding the list from two sources on every keystroke, and the untouched charge would have
 * nowhere to live between the tick and the save.
 * </p>
 */
export interface ChecklistEntry {
  damageTypeId: number;
  code: string;
  name: string;
  description: string | null;
  /** What the master publishes for this garment's category. Zero when nothing is published. */
  standardCharge: number;
  /** Null when no charge is published at all — the line is offered, unpriced. */
  chargeId: number | null;
  resolvedFromCategoryName: string | null;
  isInherited: boolean;
  ticked: boolean;
  /**
   * What this line will actually cost, defaulted from the master and editable.
   *
   * Editable because the master publishes a <em>standard</em> charge, not a fixed one: a stain that
   * lifted and one that did not are the same tick and different money. What the master buys is that
   * the clerk starts from a published figure rather than inventing one.
   */
  charge: number;
  note: string;
}

export interface InspectionDraft {
  outcome: InspectionOutcome;
  entries: ChecklistEntry[];
  notes: string;
  acknowledgedByCustomer: boolean;
}

/**
 * The draft a screen opens with: the checklist for this garment, with whatever was recorded last
 * time already ticked back on.
 *
 * <p>
 * A correction is the ordinary case rather than the exception — a clerk finds a second mark five
 * minutes later — so re-opening has to show what was said, not an empty list. The <em>frozen</em>
 * charge wins over the master's current figure for the same reason the server stores it: what the
 * customer was shown is what must be read back.
 * </p>
 */
export function draftFrom(
  checklist: readonly DamageChecklistLine[],
  existing: ReturnInspection | null,
): InspectionDraft {
  const found = new Map((existing?.findings ?? []).map((f) => [f.damageTypeId, f]));

  return {
    outcome: existing?.outcome ?? 'Clean',
    notes: existing?.notes ?? '',
    acknowledgedByCustomer: existing?.acknowledgedByCustomer ?? false,
    entries: checklist.map((line) => {
      const finding = found.get(line.damageTypeId);

      return {
        damageTypeId: line.damageTypeId,
        code: line.damageTypeCode,
        name: line.damageTypeName,
        description: line.description,
        standardCharge: line.standardCharge,
        chargeId: line.chargeId,
        resolvedFromCategoryName: line.resolvedFromCategoryName,
        isInherited: line.isInherited,
        ticked: finding !== undefined,
        charge: finding?.charge ?? line.standardCharge,
        note: finding?.note ?? '',
      };
    }),
  };
}

/**
 * Ticking a line arms its published charge; un-ticking puts the figure back rather than clearing
 * it, so a clerk who un-ticks by mistake does not also lose the number they had typed.
 */
export function toggle(draft: InspectionDraft, damageTypeId: number): InspectionDraft {
  return {
    ...draft,
    entries: draft.entries.map((entry) =>
      entry.damageTypeId === damageTypeId ? { ...entry, ticked: !entry.ticked } : entry,
    ),
  };
}

export function setCharge(
  draft: InspectionDraft,
  damageTypeId: number,
  charge: number,
): InspectionDraft {
  return {
    ...draft,
    entries: draft.entries.map((entry) =>
      entry.damageTypeId === damageTypeId ? { ...entry, charge } : entry,
    ),
  };
}

export function setNote(
  draft: InspectionDraft,
  damageTypeId: number,
  note: string,
): InspectionDraft {
  return {
    ...draft,
    entries: draft.entries.map((entry) =>
      entry.damageTypeId === damageTypeId ? { ...entry, note } : entry,
    ),
  };
}

/**
 * Choosing Clean clears the ticks.
 *
 * <p>
 * The server refuses a clean return that carries a charge rather than silently dropping the lines,
 * because the clerk meant one of the two. Doing it here means they find out by watching the ticks
 * go, at the moment they chose it, rather than by a 400 after they press Save.
 * </p>
 */
export function setOutcome(draft: InspectionDraft, outcome: InspectionOutcome): InspectionDraft {
  return outcome === 'Clean'
    ? { ...draft, outcome, entries: draft.entries.map((e) => ({ ...e, ticked: false })) }
    : { ...draft, outcome };
}

export function ticked(draft: InspectionDraft): ChecklistEntry[] {
  return draft.entries.filter((entry) => entry.ticked);
}

/**
 * What the customer is asked for.
 *
 * <p>
 * Beyond repair is the design's replacement value and not the sum of the marks, because what was
 * lost is the whole garment and no checklist can express that. The findings are still recorded —
 * they are the evidence for the write-off — which is exactly why this cannot be a plain sum.
 * </p>
 */
export function total(draft: InspectionDraft, replacementValue: number | null): number {
  if (draft.outcome === 'BeyondRepair') {
    return replacementValue ?? 0;
  }

  return ticked(draft).reduce((sum, entry) => sum + (entry.charge || 0), 0);
}

/**
 * What the server would refuse, said here so the clerk sees it before the 400 — the discipline
 * `draftProblems` and `lineProblems` already follow on the other two counters.
 */
export function inspectionProblems(
  draft: InspectionDraft,
  replacementValue: number | null,
): string[] {
  const problems: string[] = [];
  const lines = ticked(draft);

  if (draft.outcome === 'Clean' && lines.length > 0) {
    problems.push('A clean return cannot carry a charge. Change the outcome, or clear the lines.');
  }

  if (lines.some((entry) => entry.charge < 0)) {
    problems.push('A charge cannot be negative. Untick the line instead of pricing it below zero.');
  }

  if (draft.outcome === 'BeyondRepair' && replacementValue === null) {
    problems.push(
      'This design has no replacement value, so there is nothing to charge for writing it off. Set one on the design first.',
    );
  }

  // Not a refusal on either side — a shop must be able to record damage it has not priced — but it
  // is the one thing on this screen a clerk would want to know before reading a figure out loud.
  if (draft.outcome !== 'Clean' && lines.some((entry) => entry.chargeId === null)) {
    problems.push(
      'One or more ticked lines have no published charge. Type a figure, or publish one on the rate card.',
    );
  }

  return problems;
}

/** Whether Save would be accepted. Kept beside the problems so the two cannot drift. */
export function canSave(draft: InspectionDraft, replacementValue: number | null): boolean {
  return inspectionProblems(draft, replacementValue).length === 0;
}

/** The payload, built from the ticked lines only. */
export function toRequest(draft: InspectionDraft): {
  outcome: InspectionOutcome;
  findings: { damageTypeId: number; charge: number; note?: string }[];
  notes?: string;
  acknowledgedByCustomer: boolean;
} {
  return {
    outcome: draft.outcome,
    findings: ticked(draft).map((entry) => ({
      damageTypeId: entry.damageTypeId,
      charge: entry.charge || 0,
      ...(entry.note.trim() === '' ? {} : { note: entry.note.trim() }),
    })),
    ...(draft.notes.trim() === '' ? {} : { notes: draft.notes.trim() }),
    acknowledgedByCustomer: draft.acknowledgedByCustomer,
  };
}
